import type { SupportTicketListItemResponse } from "@redotech/effect-api-shared/models/shopper-ai/conversation/support-ticket";
import { SupportTicketFilterNames } from "@redotech/effect-api-shared/schema/support-ticket-filters";
import type { ArrayStringFilterData } from "@redotech/redo-model/views/advanced-filters/array-string-filter";
import { ArrayStringFilterOperator } from "@redotech/redo-model/views/advanced-filters/array-string-filter";
import type { DateFilterData } from "@redotech/redo-model/views/advanced-filters/date-filter";
import {
  DateFilterOperator,
  getAfterDateForDateFilter,
  getBeforeDateForDateFilter,
  getWithinDatesForDateFilter,
  type KnownDateFilterTimeFrame,
} from "@redotech/redo-model/views/advanced-filters/date-filter";
import type { ExistsFilterData } from "@redotech/redo-model/views/advanced-filters/exists-filter";
import { ExistsFilterOperator } from "@redotech/redo-model/views/advanced-filters/exists-filter";
import {
  AdvancedFilterType,
  FilterOperator,
  isFilterGroup,
} from "@redotech/redo-model/views/advanced-filters/generic-advanced-filter-data";
import type { NumberFilterData } from "@redotech/redo-model/views/advanced-filters/number-filter";
import { NumberFilterOperator } from "@redotech/redo-model/views/advanced-filters/number-filter";
import type {
  AdvancedFilterData,
  FilterGroup,
} from "@redotech/redo-model/views/views";
import type { Ref } from "@tanstack/react-db";
import {
  and,
  eq,
  gt,
  gte,
  ilike,
  inArray,
  isNull,
  lt,
  lte,
  not,
  or,
} from "@tanstack/react-db";

type TanstackExpression = ReturnType<typeof eq>;

/**
 * The ref type for a non-object field T (leaf node in the TanStack ref proxy tree).
 * Extracts the same type that `Ref<{ _: T }>['_']` produces at runtime.
 */
type FieldRef<T> = Ref<{ _: T }>["_"];

function isArrayStringFilter(
  filter: AdvancedFilterData,
): filter is ArrayStringFilterData {
  return filter.type === AdvancedFilterType.ARRAY_TO_STRING;
}

function isExistsFilter(
  filter: AdvancedFilterData,
): filter is ExistsFilterData {
  return filter.type === AdvancedFilterType.EXISTS;
}

function isDateFilter(filter: AdvancedFilterData): filter is DateFilterData {
  return filter.type === AdvancedFilterType.DATE;
}

function isNumberFilter(
  filter: AdvancedFilterData,
): filter is NumberFilterData {
  return filter.type === AdvancedFilterType.NUMBER;
}

/**
 * Combines an array of expressions with AND or OR, returning null for empty.
 */
function combineExpressions(
  expressions: TanstackExpression[],
  operator: FilterOperator,
): TanstackExpression | null {
  if (expressions.length === 0) {
    return null;
  }
  return expressions.reduce((acc, expr) =>
    operator === FilterOperator.AND ? and(acc, expr) : or(acc, expr),
  );
}

/**
 * Builds a TanStack expression for a scalar string field with ANY_OF / NONE_OF.
 */
function scalarFieldAnyOfNoneOf(
  fieldRef: FieldRef<string | null>,
  filter: ArrayStringFilterData,
): TanstackExpression | null {
  const values = filter.value ?? [];
  if (values.length === 0) {
    return null;
  }
  switch (filter.operator) {
    case ArrayStringFilterOperator.ANY_OF:
      return values.length === 1
        ? eq(fieldRef, values[0])
        : inArray(fieldRef, values);
    case ArrayStringFilterOperator.NONE_OF:
      return values.length === 1
        ? not(eq(fieldRef, values[0]))
        : not(inArray(fieldRef, values));
    default:
      return null;
  }
}

/**
 * Builds a TanStack expression that checks if any of the given values
 * exist in an array field on the conversation.
 */
function arrayFieldContainsAny<T extends string>(
  arrayRef: FieldRef<readonly T[]>,
  values: T[],
): TanstackExpression | null {
  if (values.length === 0) {
    return null;
  }
  if (values.length === 1) {
    return inArray(values[0], arrayRef);
  }
  const expressions = values.map((v) => inArray(v, arrayRef));
  return or(expressions[0], expressions[1], ...expressions.slice(2));
}

/**
 * Builds a TanStack expression that checks if all of the given values
 * exist in an array field on the conversation.
 */
function arrayFieldContainsAll<T extends string>(
  arrayRef: FieldRef<readonly T[]>,
  values: T[],
): TanstackExpression | null {
  if (values.length === 0) {
    return null;
  }
  if (values.length === 1) {
    return inArray(values[0], arrayRef);
  }
  const expressions = values.map((v) => inArray(v, arrayRef));
  return and(expressions[0], expressions[1], ...expressions.slice(2));
}

/**
 * Builds a TanStack expression for a date filter.
 */
function dateFilterToExpression(
  filter: DateFilterData,
  fieldRef: FieldRef<Date> | null,
): TanstackExpression | null {
  if (!fieldRef) {
    return null;
  }
  const value = filter.value as KnownDateFilterTimeFrame | null;
  if (!value) {
    return null;
  }

  try {
    switch (filter.operator) {
      case DateFilterOperator.WITHIN: {
        const dates = getWithinDatesForDateFilter(
          value,
          filter.customDate,
          undefined,
          filter.relativeTime,
        );
        if (!dates) {
          return null;
        }
        return and(gte(fieldRef, dates.start), lte(fieldRef, dates.end));
      }
      case DateFilterOperator.BEFORE: {
        const date = getBeforeDateForDateFilter(
          value,
          filter.customDate,
          undefined,
          filter.relativeTime,
        );
        if (!date) {
          return null;
        }
        return lt(fieldRef, date);
      }
      case DateFilterOperator.AFTER: {
        const date = getAfterDateForDateFilter(
          value,
          filter.customDate,
          undefined,
          filter.relativeTime,
        );
        if (!date) {
          return null;
        }
        return gt(fieldRef, date);
      }
      default:
        return null;
    }
  } catch {
    return null;
  }
}

/**
 * Builds a TanStack expression for a numeric filter against a number field.
 */
function numberFilterToExpression(
  filter: AdvancedFilterData,
  fieldRef: FieldRef<number>,
): TanstackExpression | null {
  if (!isNumberFilter(filter) || !filter.value) {
    return null;
  }
  const numbers = [...filter.value].sort((a, b) => a - b);
  switch (filter.operator) {
    case NumberFilterOperator.EQUAL:
      return eq(fieldRef, numbers[0]);
    case NumberFilterOperator.GREATER_THAN:
      return gt(fieldRef, numbers[0]);
    case NumberFilterOperator.LESS_THAN:
      return lt(fieldRef, numbers[0]);
    case NumberFilterOperator.BETWEEN:
      if (numbers.length !== 2) {
        return null;
      }
      return and(gte(fieldRef, numbers[0]), lte(fieldRef, numbers[1]));
    default:
      return null;
  }
}

/**
 * Converts a single AdvancedFilterData to a TanStack DB expression.
 *
 * Note: Only handles filters that can be applied client-side to TanStack DB.
 * Complex filters (date ranges, tags requiring joins, etc.) return null
 * and are handled server-side via OpenSearch.
 */
export function advancedFilterToTanstackExpression(
  filter: AdvancedFilterData,
  conversationRef: Ref<SupportTicketListItemResponse>,
): TanstackExpression | null {
  switch (filter.name) {
    case SupportTicketFilterNames.STATUS: {
      if (!isArrayStringFilter(filter)) {
        return null;
      }
      return scalarFieldAnyOfNoneOf(conversationRef.status, filter);
    }

    case SupportTicketFilterNames.READ_AT: {
      if (!isExistsFilter(filter)) {
        return null;
      }
      const fieldRef = conversationRef.readAt;
      const value = filter.value;

      // The EXISTS filter has both an operator and a value (boolean).
      // EXISTS + true = field is not null
      // EXISTS + false = field is null
      // NOT_EXISTS + true = field is null
      // NOT_EXISTS + false = field is not null
      switch (filter.operator) {
        case ExistsFilterOperator.EXISTS:
          return value ? not(isNull(fieldRef)) : isNull(fieldRef);
        case ExistsFilterOperator.NOT_EXISTS:
          return value ? isNull(fieldRef) : not(isNull(fieldRef));
        default:
          return null;
      }
    }

    case SupportTicketFilterNames.WORDS: {
      // WORDS is a full-text search filter that runs server-side via OpenSearch.
      // Return an ilike expression on summary so TanStack DB sees a different
      // query when the search term changes, triggering a refetch from the server.
      // This also provides basic client-side filtering on the summary field.
      if (!isArrayStringFilter(filter)) {
        return null;
      }
      const searchTerm = filter.value?.[0];
      if (!searchTerm) {
        return null;
      }
      return ilike(conversationRef.summary, `%${searchTerm}%`);
    }

    case SupportTicketFilterNames.ASSIGNEES: {
      if (!isArrayStringFilter(filter)) {
        return null;
      }
      return scalarFieldAnyOfNoneOf(conversationRef.mongoAssigneeId, filter);
    }

    case SupportTicketFilterNames.CHANNELS: {
      if (!isArrayStringFilter(filter)) {
        return null;
      }
      const channelsRef = conversationRef.channels;
      const channelValues = filter.value ?? [];

      if (channelValues.length === 0) {
        return null;
      }

      switch (filter.operator) {
        case ArrayStringFilterOperator.ANY_OF:
          return arrayFieldContainsAny(channelsRef, channelValues);
        case ArrayStringFilterOperator.NONE_OF: {
          const anyExpr = arrayFieldContainsAny(channelsRef, channelValues);
          return anyExpr ? not(anyExpr) : null;
        }
        default:
          return null;
      }
    }

    case SupportTicketFilterNames.CONVERSATION_TAGS: {
      if (!isArrayStringFilter(filter)) {
        return null;
      }
      const tagIdsRef = conversationRef.tagIds;
      const tagValues = filter.value ?? [];

      if (tagValues.length === 0) {
        return null;
      }

      switch (filter.operator) {
        case ArrayStringFilterOperator.ANY_OF:
          return arrayFieldContainsAny(tagIdsRef, tagValues);
        case ArrayStringFilterOperator.ALL_OF:
          return arrayFieldContainsAll(tagIdsRef, tagValues);
        case ArrayStringFilterOperator.NONE_OF: {
          const anyExpr = arrayFieldContainsAny(tagIdsRef, tagValues);
          return anyExpr ? not(anyExpr) : null;
        }
        default:
          return null;
      }
    }

    case SupportTicketFilterNames.LAST_CUSTOMER_MESSAGE_AT: {
      if (!isDateFilter(filter)) {
        return null;
      }
      return dateFilterToExpression(
        filter,
        conversationRef.lastCustomerMessageAt,
      );
    }

    case SupportTicketFilterNames.LAST_MERCHANT_MESSAGE_AT: {
      if (!isDateFilter(filter)) {
        return null;
      }
      return dateFilterToExpression(
        filter,
        conversationRef.lastMerchantMessageAt,
      );
    }

    case SupportTicketFilterNames.CREATED_AT: {
      if (!isDateFilter(filter)) {
        return null;
      }
      return dateFilterToExpression(filter, conversationRef.createdAt);
    }

    case SupportTicketFilterNames.CLOSED_AT: {
      if (!isDateFilter(filter)) {
        return null;
      }
      // closedAt is represented by statusUpdatedAt when status is CLOSED.
      // We filter on statusUpdatedAt and also require status === "CLOSED".
      const dateExpr = dateFilterToExpression(
        filter,
        conversationRef.statusUpdatedAt,
      );
      if (!dateExpr) {
        return null;
      }
      return and(eq(conversationRef.status, "CLOSED"), dateExpr);
    }

    case SupportTicketFilterNames.LAST_RESPONSE_AT: {
      if (!isDateFilter(filter)) {
        return null;
      }
      // lastResponseAt is the more recent of lastCustomerMessageAt and lastMerchantMessageAt.
      // We apply the date filter to both fields and OR them together,
      // since if either timestamp satisfies the filter, the conversation matches.
      const customerExpr = dateFilterToExpression(
        filter,
        conversationRef.lastCustomerMessageAt,
      );
      const merchantExpr = dateFilterToExpression(
        filter,
        conversationRef.lastMerchantMessageAt,
      );
      if (customerExpr && merchantExpr) {
        return or(customerExpr, merchantExpr);
      }
      return customerExpr ?? merchantExpr;
    }

    case SupportTicketFilterNames.CUSTOMER_TAGS:
    case SupportTicketFilterNames.MENTIONS:
    case SupportTicketFilterNames.PARTICIPANT_KEYS:
    case SupportTicketFilterNames.PRIMARY_PARTICIPANT_KEYS:
    case SupportTicketFilterNames.SHOPPER_PARTICIPANT_IDS:
    case SupportTicketFilterNames.MERCHANT_USER_PARTICIPANT_IDS:
    case SupportTicketFilterNames.REDO_USER_PARTICIPANT_IDS:
    case SupportTicketFilterNames.AGENT_PARTICIPANT_IDS:
    case SupportTicketFilterNames.SYSTEM_PARTICIPANT_KEYS:
      return null;

    case SupportTicketFilterNames.NUM_SENT_MESSAGES: {
      return numberFilterToExpression(filter, conversationRef.numSentMessages);
    }

    case SupportTicketFilterNames.NUM_DRAFTS: {
      return numberFilterToExpression(filter, conversationRef.numDrafts);
    }

    case SupportTicketFilterNames.NUM_OUTBOUND_MESSAGES: {
      return numberFilterToExpression(
        filter,
        conversationRef.numOutboundMessages,
      );
    }

    case SupportTicketFilterNames.NUM_CUSTOMER_MESSAGES: {
      return numberFilterToExpression(
        filter,
        conversationRef.numCustomerMessages,
      );
    }

    case SupportTicketFilterNames.NUM_MERCHANT_SENT_MESSAGES: {
      return numberFilterToExpression(
        filter,
        conversationRef.numMerchantSentMessages,
      );
    }

    case SupportTicketFilterNames.NUM_AI_SENT_MESSAGES: {
      return numberFilterToExpression(
        filter,
        conversationRef.numAiSentMessages,
      );
    }

    case SupportTicketFilterNames.NUM_AUTO_REPLY_SENT_MESSAGES: {
      return numberFilterToExpression(
        filter,
        conversationRef.numAutoReplySentMessages,
      );
    }

    case SupportTicketFilterNames.NUM_SYSTEM_SENT_MESSAGES: {
      return numberFilterToExpression(
        filter,
        conversationRef.numSystemSentMessages,
      );
    }

    case SupportTicketFilterNames.NUM_INTERNAL_NOTES: {
      return numberFilterToExpression(filter, conversationRef.numInternalNotes);
    }

    default:
      return null;
  }
}

/**
 * Converts a FilterGroup to a TanStack DB expression, handling AND/OR logic.
 *
 * Only filters that can be applied client-side will be included.
 * Server-side-only filters are skipped (they're handled via OpenSearch).
 */
export function filterGroupToTanstackExpression(
  group: FilterGroup,
  conversationRef: Ref<SupportTicketListItemResponse>,
): TanstackExpression | null {
  const expressions: TanstackExpression[] = [];

  for (const clause of group.clauses) {
    if (isFilterGroup(clause)) {
      // Recursively process nested groups
      const nestedExpr = filterGroupToTanstackExpression(
        clause,
        conversationRef,
      );
      if (nestedExpr !== null) {
        expressions.push(nestedExpr);
      }
    } else {
      // Process leaf filter
      const expr = advancedFilterToTanstackExpression(clause, conversationRef);
      if (expr !== null) {
        expressions.push(expr);
      }
    }
  }

  return combineExpressions(expressions, group.operator);
}

/**
 * Converts a flat array of AdvancedFilterData to a TanStack DB expression.
 * All filters are combined with AND logic.
 *
 * This is a convenience function for when you don't have a FilterGroup.
 */
export function filtersToTanstackExpression(
  filters: AdvancedFilterData[],
  conversationRef: Ref<SupportTicketListItemResponse>,
): TanstackExpression | null {
  const expressions: TanstackExpression[] = [];

  for (const filter of filters) {
    const expr = advancedFilterToTanstackExpression(filter, conversationRef);
    if (expr !== null) {
      expressions.push(expr);
    }
  }

  return combineExpressions(expressions, FilterOperator.AND);
}
